import React, { useState, useEffect } from 'react';
import { db, auth } from '../FirebaseConfig';
import { ToastContainer, toast } from 'react-toastify';
import { Search, MessageSquare, Phone } from 'lucide-react';
import 'react-toastify/dist/ReactToastify.css';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FaPlus, FaTimes, FaSpinner, FaUpload, FaImage } from 'react-icons/fa';

const Toast = ({ message, type, onClose }) => {
  const bgColor = {
    info: 'bg-blue-600',
    success: 'bg-green-600',
    error: 'bg-red-600',
    warning: 'bg-yellow-600',
  };

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 20, opacity: 0 }}
      className={`fixed bottom-8 left-1/2 -translate-x-1/2 p-4 rounded-xl text-white shadow-xl z-50 ${bgColor[type]}`}
    >
      {message}
    </motion.div>
  );
};

const Service = (props) => {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    phone: '',
    location: '', // Added location for better service info
    image: null,
    imagePreview: null // For previewing selected image
  });

  const [services, setServices] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false); // Separate state for image upload progress
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [imageLoadStates, setImageLoadStates] = useState({}); // Track loading state for each service image
  const [visiblePhones, setVisiblePhones] = useState({});

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image' && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          image: file,
          imagePreview: reader.result
        });
      };
      reader.readAsDataURL(file);
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const fetchServices = async () => {
    const querySnapshot = await getDocs(collection(db, 'services'));
    const serviceList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setServices(serviceList);
    // Initialize loading states for all images
    const initialLoadStates = {};
    serviceList.forEach(service => {
      initialLoadStates[service.id] = true; // Start as loading
    });
    setImageLoadStates(initialLoadStates);
  };

  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', 'unsigned_ibuyunn'); // Replace with your preset
    data.append('cloud_name', 'dm8uirujp'); // Assuming your cloud name

    setImageUploading(true);
    const res = await axios.post('https://api.cloudinary.com/v1_1/dm8uirujp/image/upload', data);
    setImageUploading(false);
    return res.data.secure_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image) return toast.error('Please upload an image');
    try {
      setUploading(true);
      const imageUrl = await uploadToCloudinary(formData.image);

      await addDoc(collection(db, 'services'), {
        name: formData.name,
        title: formData.title,
        description: formData.description,
        phone: formData.phone,
        location: formData.location,
        imageUrl,
        userId: auth.currentUser?.uid,
        createdAt: serverTimestamp()
      });

      setFormData({
        name: '',
        title: '',
        description: '',
        phone: '',
        location: '',
        image: null,
        imagePreview: null
      });

      setShowForm(false);
      fetchServices();
      toast.success('Service uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, 'services', id));
    fetchServices();
  };

  const handleImageLoad = (serviceId) => {
    setImageLoadStates(prev => ({ ...prev, [serviceId]: false }));
  };

  const handleImageError = (serviceId) => {
    setImageLoadStates(prev => ({ ...prev, [serviceId]: false }));
    // Optionally show a placeholder or error icon
  };

  const togglePhoneVisibility = (serviceId) => {
    setVisiblePhones(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const handleMessage = (phone) => {
    const message = encodeURIComponent('I found your service on uCart');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  useEffect(() => {
    fetchServices();
  }, []);

  return (
    <div className="min-h-screen p-6 bg-transparent text-white relative">
      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-4xl font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-green-600 drop-shadow-lg"
      >
        Services Marketplace
      </motion.h1>

      <div className="flex flex-col items-center mb-8">
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-600 text-white px-4 py-2 rounded-md text-center mb-2 max-w-md"
          >
            {errorMessage}
          </motion.div>
        )}

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            if (!props.isSigned) {
              setErrorMessage('You must be logged in to share your service.');
              setTimeout(() => setErrorMessage(''), 3000);
            } else {
              setShowForm(!showForm);
            }
          }}
          className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-3 px-6 rounded-full shadow-lg flex items-center gap-2 transition-all"
        >
          {showForm ? <><FaTimes /> Close Form</> : <><FaPlus /> Add Service</>}
        </motion.button>
      </div>

      {showForm && (
        <motion.form
          onSubmit={handleSubmit}
          className="bg-white/10 backdrop-blur-xl p-8 rounded-2xl max-w-xl mx-auto shadow-2xl space-y-6 border border-white/20"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your Name" required className="w-full p-3 rounded-lg bg-white/5 text-white border border-white/20 focus:border-green-400 focus:outline-none transition-all" />
          <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="Service Title" required className="w-full p-3 rounded-lg bg-white/5 text-white border border-white/20 focus:border-green-400 focus:outline-none transition-all" />
          <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Description" required className="w-full p-3 rounded-lg bg-white/5 text-white border border-white/20 focus:border-green-400 focus:outline-none transition-all h-32" />
          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number" required className="w-full p-3 rounded-lg bg-white/5 text-white border border-white/20 focus:border-green-400 focus:outline-none transition-all" />
          <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="Location (City/Area)" className="w-full p-3 rounded-lg bg-white/5 text-white border border-white/20 focus:border-green-400 focus:outline-none transition-all" />
          
          {/* Image input with preview */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Upload Service Image</label>
            <div className="relative border border-white/20 rounded-lg p-4 bg-white/5 cursor-pointer hover:border-green-400/50 transition-all">
              <input
                type="file"
                accept="image/*"
                name="image"
                onChange={handleChange}
                required
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              {formData.imagePreview ? (
                <div className="relative">
                  <img src={formData.imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                  {imageUploading && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-lg">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="text-green-400"
                      >
                        <FaSpinner size={32} />
                      </motion.div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center text-gray-400">
                  <FaImage size={40} className="mb-2 text-green-400" />
                  <p className="text-sm font-medium">Click to upload or drag image</p>
                </div>
              )}
            </div>
          </div>

          <motion.button 
            type="submit" 
            disabled={uploading || imageUploading} 
            className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-3 rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {uploading || imageUploading ? (
              <>
                <FaSpinner className="animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <FaUpload />
                <span>Upload Service</span>
              </>
            )}
          </motion.button>
        </motion.form>
      )}

      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-3xl font-bold mt-12 mb-6 text-center text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-green-600 drop-shadow-lg"
      >
        Available Services
      </motion.h2>

      <div className="relative mb-6 max-w-xl mx-auto">
        <motion.input
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          type="text"
          placeholder="Search services..."
          className="w-full p-4 rounded-full bg-white/10 text-white pl-12 pr-6 outline-none border border-white/20 backdrop-blur-md focus:border-green-400 transition-all shadow-lg focus:shadow-green-400/20"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Search className="absolute top-1/2 left-4 -translate-y-1/2 text-gray-400" size={20} />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {services
          .filter(service =>
            service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            service.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            service.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map(service => {
            const isLoading = imageLoadStates[service.id];
            const isPhoneVisible = visiblePhones[service.id];
            return (
              <motion.div
                key={service.id}
                className="bg-white/10 backdrop-blur-xl p-6 rounded-2xl shadow-xl cursor-pointer overflow-hidden border border-white/20 hover:border-green-400/50 hover:shadow-green-400/20 transition-all duration-300 flex flex-col"
                whileHover={{ scale: 1.03 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="relative h-48 w-full rounded-xl mb-4 overflow-hidden bg-gradient-to-br from-gray-800/50 to-gray-900/50 shadow-inner">
                  {isLoading ? (
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ opacity: 1 }}
                      animate={{ 
                        backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                        transition: { duration: 2, repeat: Infinity }
                      }}
                      style={{ backgroundSize: '200% 200%' }}
                    >
                      <FaSpinner className="animate-spin text-green-400" size={32} />
                    </motion.div>
                  ) : null}
                  <img 
                    src={service.imageUrl} 
                    alt={service.title} 
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                    onLoad={() => handleImageLoad(service.id)}
                    onError={() => handleImageError(service.id)}
                  />
                </div>
                
                <h3 className="text-xl font-bold text-green-300 mb-2 truncate">{service.title}</h3>
                <p className="text-gray-300 text-sm mb-4 line-clamp-3">{service.description}</p>
                
                <div className="space-y-3 mt-auto">
                  {service.location && (
                    <p className="text-gray-400 text-sm flex items-center gap-2">
                      <span className="text-green-400">📍</span> {service.location}
                    </p>
                  )}
                  <p className="text-gray-400 text-sm flex items-center gap-2">
                    <span className="text-green-400">👤</span> {service.name}
                  </p>
                  
                  <div className="flex flex-col gap-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => togglePhoneVisibility(service.id)}
                      className="w-full bg-white/5 hover:bg-white/10 text-green-400 font-medium py-3 rounded-xl flex items-center justify-center gap-2 border border-white/20 transition-all"
                    >
                      <Phone size={18} />
                      {isPhoneVisible ? service.phone : 'Show Phone Number'}
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleMessage(service.phone)}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-xl flex items-center justify-center gap-2 transition-all shadow-md"
                    >
                      <MessageSquare size={18} />
                      Message on WhatsApp
                    </motion.button>
                  </div>
                </div>

                {auth.currentUser?.uid === service.userId && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(service.id);
                    }}
                    className="text-red-400 mt-4 text-sm font-medium underline hover:text-red-300 transition-colors w-full text-center"
                  >
                    Delete Service
                  </motion.button>
                )}
              </motion.div>
            );
          })}
      </div>
      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-600 text-white px-4 py-2 rounded-md text-center max-w-md mx-auto mb-4"
        >
          {errorMessage}
        </motion.div>
      )}

      {selectedService && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setSelectedService(null)}
        >
          <motion.div
            className="bg-white/10 backdrop-blur-xl p-8 rounded-2xl text-white max-w-md w-full shadow-2xl border border-white/20"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            onClick={e => e.stopPropagation()}
          >
            <img src={selectedService.imageUrl} alt={selectedService.title} className="rounded-2xl mb-6 w-full h-64 object-cover shadow-lg" />
            <h2 className="text-2xl font-bold text-green-300 mb-3">{selectedService.title}</h2>
            <p className="text-gray-200 mb-4">{selectedService.description}</p>
            <div className="space-y-2 mb-6">
              <p className="flex items-center gap-2 text-gray-200">
                <Phone size={18} className="text-green-400" /> {selectedService.phone}
              </p>
              {selectedService.location && (
                <p className="flex items-center gap-2 text-gray-200">
                  <span className="text-green-400">📍</span> {selectedService.location}
                </p>
              )}
              <p className="flex items-center gap-2 text-gray-200">
                <span className="text-green-400">👤</span> {selectedService.name}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-xl transition-all shadow-md"
              onClick={() => handleMessage(selectedService.phone)}
            >
              Message on WhatsApp
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-transparent border border-white/20 text-gray-200 font-medium py-3 rounded-xl mt-3 hover:bg-white/5 transition-all"
              onClick={() => setSelectedService(null)}
            >
              Close
            </motion.button>
          </motion.div>
        </motion.div>
      )}
      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  )
};

export default Service;